import { GoogleGenAI, Type } from "@google/genai";
import { geminiModelName } from "./firebase";
import {
  type AIMessage,
  type ParsedAIResponse,
  type SearchAIResponse,
  type User,
  type ActivityRequest,
  VibeCheckUser,
} from "../types";
import { CommonFunctions } from "../utils/commonUtils";
import {
  generateActivitySuggestion as generateActivitySuggestionCallable,
  generateActivitySearchIdeas as generateActivitySearchIdeasCallable,
} from "./functionsApi";

import { crashlytics, getApiKeys } from "./firebase";

// Prompt template cache
const promptCache: Record<string, string> = {};

// Helper to load prompt templates from files
async function loadPrompt(name: string): Promise<string> {
  if (promptCache[name]) return promptCache[name];
  try {
    const response = await fetch(`/prompts/${name}.txt`);

    // Check if response is OK (status 200-299)
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const text = await response.text();

    // Validate we got actual prompt text, not HTML
    if (
      text.trim().startsWith("<!DOCTYPE") ||
      text.trim().startsWith("<html")
    ) {
      throw new Error(`Received HTML instead of prompt file for ${name}`);
    }

    if (text.trim().length === 0) {
      throw new Error(`Empty prompt file for ${name}`);
    }

    promptCache[name] = text;
    return text;
  } catch (error) {
    crashlytics.logError(
      `Error loading prompt ${name}: ${JSON.stringify(error?.message)}`,
      `geminiService.ts`,
      error,
    );
    return ""; // Fallback to inline prompts if file loading fails
  }
}

/**
 * Gemini API Service with robust error handling
 *
 * Common API Errors and Solutions:
 * - 503 UNAVAILABLE: Google's servers are overloaded. This is temporary - wait and retry.
 * - 429 RATE_LIMIT: Too many requests. Implement backoff strategy.
 * - 400 BAD_REQUEST: Invalid request format or parameters.
 * - 401 UNAUTHORIZED: Invalid API key.
 *
 * The service gracefully handles these errors by providing fallback responses
 * instead of throwing errors, ensuring smooth user experience.
 */

const getClient = async (): Promise<GoogleGenAI> => {
  const { GOOGLE_API_KEY } = await getApiKeys();
  if (!GOOGLE_API_KEY) {
    throw new Error(
      "API Key not found. Please set the `API_KEY` environment variable.",
    );
  }
  return new GoogleGenAI({ apiKey: GOOGLE_API_KEY });
};

const responseSchema = {
  type: Type.OBJECT,
  properties: {
    category: {
      type: Type.STRING,
      description:
        "CRITICAL: Choose the most accurate category based on the PRIMARY activity. Use these specific rules:\n\n" +
        "🎬 MOVIES: Cinema, film screenings, movie theaters, watching films, film festivals\n" +
        "🍽️ DINING: Restaurants, eating meals, food tours, cooking classes, brunch, dinner dates\n" +
        "🎮 GAMES: Board games, video games, escape rooms, trivia nights, arcade, card games, party games\n" +
        "🌲 OUTDOOR: Hiking, parks, beaches, camping, sports, running, cycling, nature activities\n" +
        "🍻 DRINKS: Bars, pubs, cocktails, coffee shops, wine tasting, brewery tours, happy hour\n" +
        "💪 FITNESS: Gym, yoga, pilates, dance classes, martial arts, swimming, rock climbing\n\n" +
        "EXAMPLES:\n" +
        "- 'Coffee meetup' → drinks (primary focus is beverages)\n" +
        "- 'Dinner and drinks' → dining (meal is primary)\n" +
        "- 'Board game night at cafe' → games (gaming is primary)\n" +
        "- 'Beach volleyball' → outdoor (sport in nature)\n" +
        "- 'Movie and popcorn' → movies (cinema is primary)\n" +
        "- 'Gym session' → fitness (exercise is primary)",
      enum: ["movies", "dining", "games", "outdoor", "drinks", "fitness"],
    },
    title: {
      type: Type.STRING,
      description:
        "A progressively enhanced title that becomes more specific as details are gathered. Start simple (e.g., 'Coffee Chat'), then add context as you learn more (e.g., 'Morning Coffee Chat at Marina Bay'). Include timing, location, or group context when appropriate.",
    },
    description: {
      type: Type.STRING,
      description:
        "A progressively detailed description that evolves with the conversation. Start with basic activity info, then add contextual details about the setting, timing, group dynamics, and what makes this activity appealing. Maximum 500 characters.",
    },
    location: {
      type: Type.STRING,
      description: `A specific, verifiable, and popular location in Singapore for this activity that can be found on Google Maps. Provide a real address or landmark name, e.g., 'ION Orchard' or '1 Cluny Rd, Singapore Botanic Gardens'. Avoid vague areas like 'downtown'.`,
    },
    suggestedDateTime: {
      type: Type.STRING,
      description:
        "A future date and time for the activity in valid ISO 8601 format (e.g., 'YYYY-MM-DDTHH:MM:SS.sssZ'). Be specific about the date, time, and timezone. The date must be in the future.",
    },
    duration: {
      type: Type.INTEGER,
      description:
        "The estimated duration of the activity in minutes (e.g., 90 for 1.5 hours).",
    },
    groupSize: {
      type: Type.INTEGER,
      description:
        "The final confirmed group size for the activity. This must be an integer of 2 or greater (e.g., 2, 4, 6). A solo activity is not allowed.",
    },

    followUpQuestions: {
      type: Type.ARRAY,
      description:
        "The next logical question to ask the user to get more details. Questions should be relevant and make sense in context. If you have a full draft ready for review, use this field to ask for confirmation (e.g., 'How does this look?'). If all details are confirmed, return an empty array. AVOID nonsensical questions that mix up fields (e.g., don't ask for 'time for people' or 'location for duration').",
      items: { type: Type.STRING },
    },
    suggestedReplies: {
      type: Type.ARRAY,
      description:
        "A list of 2-3 short, clickable reply suggestions that make logical sense for the user to guide the conversation. Each reply should be contextually appropriate (e.g., specific times like '7pm', specific places like 'Marina Bay', or actions like 'Post it!'). For group size, use specific numbers like '2 people', '4 people', '6 people' instead of ambiguous terms like 'small group' or 'large group'. AVOID mixing field types (e.g., don't suggest '4pm for people' or 'Singapore for time').",
      items: { type: Type.STRING },
    },
  },
};

// Helper function to extract current form data from conversation history
function extractFormContext(conversationHistory: AIMessage[]): string {
  const lastAiMessage = [...conversationHistory]
    .filter(
      (msg) =>
        msg.role === "model" &&
        typeof msg.content === "object" &&
        msg.content !== null,
    )
    .pop();

  if (!lastAiMessage || typeof lastAiMessage.content !== "object") {
    return "";
  }

  const content = lastAiMessage.content as any;
  const formData = [];

  if (content.title) formData.push(`Title: "${content.title}"`);
  if (content.location) formData.push(`Location: "${content.location}"`);
  if (content.description)
    formData.push(`Description: "${content.description}"`);
  if (content.suggestedDateTime) {
    const date = new Date(content.suggestedDateTime);
    formData.push(
      `Date/Time: ${date.toLocaleDateString()} at ${date.toLocaleTimeString()}`,
    );
  }
  if (content.duration) formData.push(`Duration: ${content.duration} minutes`);
  if (content.groupSize)
    formData.push(`Group Size: ${content.groupSize} people`);
  if (content.category) formData.push(`Category: ${content.category}`);

  if (formData.length > 0) {
    return `\n\n**CURRENT ACTIVITY PROGRESS:**\n${formData.join("\n")}\n\n**IMPORTANT:** The user has existing form data above. Always preserve and build upon this information. Focus on completing missing details or refining existing ones rather than starting fresh.`;
  }

  return "";
}

// Helper function to detect low-quality AI responses during streaming
function isLowQualityStreamContent(
  chunk: string,
  responseBuffer: string,
): boolean {
  const fullContent = responseBuffer + chunk;

  // Only check after we have substantial content to avoid false positives
  if (fullContent.length < 200) {
    return false;
  }

  // Check for repetitive patterns that indicate AI confusion (much more lenient)
  const words = fullContent.toLowerCase().split(/\s+/);
  const wordCounts = words.reduce(
    (acc, word) => {
      if (word.length > 4) {
        // Only count longer significant words
        acc[word] = (acc[word] || 0) + 1;
      }
      return acc;
    },
    {} as Record<string, number>,
  );

  // Only flag if a word appears more than 10 times (much more lenient)
  const hasExcessiveRepetition = Object.values(wordCounts).some(
    (count) => count > 10,
  );

  // Check for severe AI hallucination patterns (only the most obvious ones)
  const severeHallucinationPatterns = [
    /\{.*\{.*\{.*\{/g, // 4+ nested braces (severely malformed JSON)
    /"{5,}/g, // 5+ consecutive quotes
    /(follow.*up.*question.*follow.*up.*question.*follow.*up.*question)/i, // 3+ repetitions
  ];

  const hasSevereHallucination = severeHallucinationPatterns.some((pattern) =>
    pattern.test(fullContent),
  );

  // Check for extremely long responses without any structure
  const isExtremelyLong =
    fullContent.length > 5000 &&
    !fullContent.includes("followUpQuestions") &&
    !fullContent.includes("suggestedReplies");

  // Check for responses that are almost entirely symbols (very lenient)
  const symbolRatio =
    (fullContent.match(/[{}[\]",:\s]/g) || []).length / fullContent.length;
  const isAlmostAllSymbols = symbolRatio > 0.95 && fullContent.length > 100;

  // Only trigger on severe issues
  return (
    hasExcessiveRepetition ||
    hasSevereHallucination ||
    isExtremelyLong ||
    isAlmostAllSymbols
  );
}

// Streaming version: returns an async generator yielding partial or full responses as they arrive
export async function* generateActivitySuggestionStream(
  conversationHistory: AIMessage[],
  signal?: AbortSignal,
): AsyncGenerator<string, void, unknown> {
  try {
    if (signal?.aborted) {
      yield JSON.stringify({
        followUpQuestions: ["Let's try that again. What would you like to do?"],
        suggestedReplies: [
          "Coffee & chat",
          "Outdoor activity",
          "Entertainment",
          "Something different",
        ],
      });
      return;
    }

    const result = await generateActivitySuggestionCallable({
      conversationHistory,
    });

    if (signal?.aborted) {
      yield JSON.stringify({
        followUpQuestions: ["Let's try that again. What would you like to do?"],
        suggestedReplies: [
          "Coffee & chat",
          "Outdoor activity",
          "Entertainment",
          "Something different",
        ],
      });
      return;
    }

    yield JSON.stringify(result.response || {});
  } catch (error) {
    crashlytics.logError(
      `Error in generateActivitySuggestionStream: ${JSON.stringify(error?.message)}`,
      "geminiService.ts",
      error,
    );

    yield JSON.stringify({
      followUpQuestions: [
        "I'm having trouble with the AI service. Let's try something specific!",
      ],
      suggestedReplies: [
        "Coffee meetup",
        "Movie night",
        "Outdoor activity",
        "Create manually",
      ],
    } satisfies ParsedAIResponse);
  }
}

// Firebase AI version: returns an async generator using Firebase Vertex AI
export async function* generateActivitySuggestionStreamFirebase(
  conversationHistory: AIMessage[],
  signal?: AbortSignal,
): AsyncGenerator<string, void, unknown> {
  // Kept as compatibility alias while callers migrate to the auto stream.
  yield* generateActivitySuggestionStream(conversationHistory, signal);
}

// Streaming version: returns an async generator yielding partial or full responses as they arrive
export async function* searchActivitiesStream(
  query: string,
  user?: User,
  userActivities?: ActivityRequest[],
  searchHistory?: string[],
): AsyncGenerator<SearchAIResponse, void, unknown> {
  try {
    const response = await generateActivitySearchIdeasCallable({
      query,
      user,
      userActivities,
      searchHistory,
    });
    yield response;
  } catch (error) {
    crashlytics.logError(
      `Error in searchActivitiesStream: ${JSON.stringify(error?.message)}`,
      "geminiService.ts",
      error,
    );
    yield { ideas: [] };
  }
}

// Streaming version: returns an async generator yielding partial or full results as they arrive
export async function* getForYouPageDataStream(
  user: User,
  allUsers: User[],
  allActivities: ActivityRequest[],
  candidateActivities: ActivityRequest[],
  recentlyCreatedActivities: ActivityRequest[],
): AsyncGenerator<
  {
    recommendations: (ActivityRequest & { reason: string })[];
    vibeCheckUsers: VibeCheckUser[];
  },
  void,
  unknown
> {
  if (allUsers.length <= 1) {
    yield { recommendations: [], vibeCheckUsers: [] };
    return;
  }
  try {
    const client = await getClient();
    const forYouSchema = {
      type: Type.OBJECT,
      properties: {
        recommendations: {
          type: Type.ARRAY,
          description:
            "An array of up to 5 recommended activities based on user preferences.",
          items: {
            type: Type.OBJECT,
            properties: {
              id: {
                type: Type.STRING,
                description: "The string ID of the activity.",
              },
              reason: {
                type: Type.STRING,
                description:
                  "A short, friendly reason why this is recommended. E.g., 'Because you enjoy outdoor activities.' or 'Similar to events you've joined.'",
              },
            },
            required: ["id", "reason"],
          },
        },
        vibeChecks: {
          type: Type.ARRAY,
          description:
            "An array of compatibility vibe checks for up to 6 target users.",
          items: {
            type: Type.OBJECT,
            properties: {
              userId: {
                type: Type.STRING,
                description: "The string ID of the target user.",
              },
              score: {
                type: Type.INTEGER,
                description: "A compatibility score from 0 to 100.",
              },
              summary: {
                type: Type.STRING,
                description:
                  "A friendly, one-sentence summary explaining the score based on shared activity interests.",
              },
              one_liner: {
                type: Type.STRING,
                description:
                  "A very short, catchy phrase about the connection.",
              },
            },
            required: ["userId", "score", "summary", "one_liner"],
          },
        },
      },
      required: ["recommendations", "vibeChecks"],
    };

    // Build enhanced user context using profile if available
    let userProfileContext = "";
    if (user.userProfile && user.userProfile.lastUpdated) {
      const profileAge =
        Date.now() - new Date(user.userProfile.lastUpdated).getTime();
      const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;

      if (profileAge < sevenDaysMs) {
        // Use AI-generated profile for much better recommendations
        userProfileContext = `\n\n**AI-GENERATED USER PROFILE:**\n${user.userProfile.summary}\n\n`;
        userProfileContext += `**DETAILED PREFERENCES:**\n`;

        if (
          user.userProfile.preferences.categories &&
          user.userProfile.preferences.categories.length > 0
        ) {
          const topCats = user.userProfile.preferences.categories
            .slice(0, 5)
            .map((c) => `${c.name} (score: ${c.score})`)
            .join(", ");
          userProfileContext += `- Top activity categories: ${topCats}\n`;
        }

        if (
          user.userProfile.preferences.locations &&
          user.userProfile.preferences.locations.length > 0
        ) {
          const topLocs = user.userProfile.preferences.locations
            .slice(0, 5)
            .map((l) => `${l.area} (${l.frequency} visits)`)
            .join(", ");
          userProfileContext += `- Favorite locations: ${topLocs}\n`;
        }

        if (user.userProfile.preferences.timePreferences) {
          const timePrefs = Object.entries(
            user.userProfile.preferences.timePreferences,
          )
            .sort(([, a], [, b]) => (b as number) - (a as number))
            .map(([time, score]) => `${time} (${score}%)`)
            .join(", ");
          userProfileContext += `- Time preferences: ${timePrefs}\n`;
        }

        userProfileContext += `- Group size preference: ${user.userProfile.preferences.groupSizePreference}\n`;
        userProfileContext += `- Activity role: ${user.userProfile.preferences.role}\n`;
        userProfileContext += `- Activity style: ${user.userProfile.preferences.activityStyle}\n`;

        userProfileContext += `\n**BEHAVIORAL INSIGHTS:**\n`;
        userProfileContext += `- Social behavior: ${user.userProfile.insights.socialBehavior}\n`;
        userProfileContext += `- Activity patterns: ${user.userProfile.insights.activityPatterns}\n`;
        userProfileContext += `- Unique traits: ${user.userProfile.insights.uniqueTraits}\n`;
        userProfileContext += `- Commitment level: ${user.userProfile.insights.commitmentLevel}\n`;
        userProfileContext += `- Engagement style: ${user.userProfile.insights.engagementStyle}\n`;

        userProfileContext += `\n**IMPORTANT:** Use this comprehensive profile to make highly personalized recommendations. Match activities to their preferred categories, locations, times, and group sizes. Consider their role (${user.userProfile.preferences.role}) when recommending activities. For vibe checks, match users with similar social behaviors and activity patterns.\n`;
      }
    }

    const simplifiedPastActivities = allActivities
      .filter((a) => a.participants.some((p) => p.id === user.id))
      .map((a) => ({ title: a.title, category: a.category }));

    const simplifiedCandidateActivities = candidateActivities.map((a) => ({
      id: a.id,
      title: a.title,
      category: a.category,
      description: a.description,
    }));

    const simplifiedRecentActivities = recentlyCreatedActivities.map((a) => ({
      title: a.title,
      category: a.category,
      description: a.description,
      location: a.location,
    }));

    const otherUsers = allUsers.filter(
      (u) => u.id !== user.id && !user.following?.includes(u.id),
    );
    const targetUsersForVibeCheck = otherUsers
      .sort(() => 0.5 - Math.random())
      .slice(0, 6);

    const getUserActivityNames = (userId: string) =>
      allActivities
        .filter((a) => a.participants.some((p) => p.id === userId))
        .map((a) => `${a.title} (${a.category})`);

    const targetUsersData = targetUsersForVibeCheck.map((u) => ({
      id: u.id,
      username: u.username,
      activities: getUserActivityNames(u.id),
    }));

    // Load prompt template
    const promptTemplate = await loadPrompt("for-you-recommendations");
    if (!promptTemplate) {
      throw new Error("Failed to load for-you-recommendations prompt");
    }

    const prioritizationText = userProfileContext
      ? "PRIORITIZE activities matching their AI-generated profile preferences (categories, locations, times, group sizes)."
      : "For new users with no activity history, prioritize matching their stated passions and interests.";

    const vibeCheckCriteria = userProfileContext
      ? ", similar social behaviors, and matching activity patterns from their profiles"
      : " AND similar passions/persona";

    const contents = promptTemplate
      .replace("{{USERNAME}}", user.username)
      .replace(
        "{{AGE}}",
        CommonFunctions.calculateAge(user.dateOfBirth).toString(),
      )
      .replace("{{GENDER}}", user.gender || "Not specified")
      .replace("{{LOCATION}}", user.location)
      .replace("{{BIO}}", user.bio || "No bio yet")
      .replace(
        "{{PASSIONS}}",
        user.passions && user.passions.length > 0
          ? user.passions.join(", ")
          : "Not specified",
      )
      .replace("{{PERSONA}}", user.personaTitle || "Exploring")
      .replace(
        "{{PAST_ACTIVITIES}}",
        simplifiedPastActivities.length > 0
          ? JSON.stringify(simplifiedPastActivities, null, 2)
          : "None yet (Use passions/bio to infer preferences)",
      )
      .replace("{{USER_PROFILE_CONTEXT}}", userProfileContext)
      .replace("{{PRIORITIZATION}}", prioritizationText)
      .replace(
        "{{CANDIDATE_ACTIVITIES}}",
        simplifiedCandidateActivities.length > 0
          ? JSON.stringify(simplifiedCandidateActivities, null, 2)
          : "No activities available to recommend.",
      )
      .replace(
        "{{RECENTLY_CREATED_ACTIVITIES}}",
        simplifiedRecentActivities.length > 0
          ? JSON.stringify(simplifiedRecentActivities, null, 2)
          : "No recent activities available.",
      )
      .replace("{{VIBE_CHECK_CRITERIA}}", vibeCheckCriteria)
      .replace(
        "{{TARGET_USERS}}",
        targetUsersData.length > 0
          ? JSON.stringify(targetUsersData, null, 2)
          : "No users available for vibe check.",
      );

    crashlytics.log(`For You Prompt Length: ${contents.length} characters`);
    crashlytics.log(`For You Prompt Contents: ${contents}`);
    const stream = await client.models.generateContentStream({
      model: geminiModelName,
      contents: contents,
      config: {
        responseMimeType: "application/json",
        responseSchema: forYouSchema,
        temperature: 0.7,
      },
    });

    let fullText = "";
    for await (const chunk of stream) {
      if (chunk.text) fullText += chunk.text;
      // Try to parse the current accumulated text as JSON
      try {
        const responseJson = JSON.parse(fullText);
        let recommendations: (ActivityRequest & { reason: string })[] = [];
        if (responseJson && Array.isArray(responseJson.recommendations)) {
          const candidateMap = new Map(
            candidateActivities.map((a) => [a.id, a]),
          );
          recommendations = responseJson.recommendations
            .map((rec: any) => {
              const activity = candidateMap.get(rec.id);
              if (activity) {
                return {
                  ...activity,
                  reason: rec.reason || "You might like this!",
                };
              }
              return null;
            })
            .filter(
              (a: any): a is ActivityRequest & { reason: string } => a !== null,
            );
        }
        let vibeCheckUsers: VibeCheckUser[] = [];
        if (responseJson && Array.isArray(responseJson.vibeChecks)) {
          const userMap = new Map(
            targetUsersForVibeCheck.map((u) => [u.id, u]),
          );
          vibeCheckUsers = responseJson.vibeChecks
            .map((vibe: any) => {
              const targetUser = userMap.get(vibe.userId);
              if (targetUser) {
                return {
                  user: targetUser,
                  vibe: {
                    score: vibe.score,
                    summary: vibe.summary,
                    one_liner: vibe.one_liner,
                  },
                };
              }
              return null;
            })
            .filter(
              (item: VibeCheckUser | null): item is VibeCheckUser =>
                item !== null,
            );
        }
        yield { recommendations, vibeCheckUsers };
      } catch (e) {
        // Not yet valid JSON, keep accumulating
      }
    }
  } catch (error) {
    crashlytics.logError(
      `Error in generateActivitySuggestionStreamFirebase: ${JSON.stringify(error?.message)}`,
      "geminiService.ts",
      error,
    );
    yield { recommendations: [], vibeCheckUsers: [] };
  }
}

// Helper function to choose the appropriate AI service
export async function* generateActivitySuggestionStreamAuto(
  conversationHistory: AIMessage[],
  signal?: AbortSignal,
): AsyncGenerator<string, void, unknown> {
  try {
    yield* generateActivitySuggestionStream(conversationHistory, signal);
  } catch (error) {
    crashlytics.logError(
      `Error in generateActivitySuggestionStreamAuto: ${JSON.stringify(error?.message)}`,
      "geminiService.ts",
      error,
    );

    // Final fallback with helpful error response
    const errorResponse: ParsedAIResponse = {
      followUpQuestions: [
        "I'm having trouble with the AI service. Let's try something specific!",
      ],
      suggestedReplies: [
        "Coffee meetup",
        "Movie night",
        "Outdoor activity",
        "Create manually",
      ],
    };

    yield JSON.stringify(errorResponse);
  }
}

// Smart category detection helper function
export function detectCategoryFromText(text: string): string {
  const lowercaseText = text.toLowerCase();

  // Category keyword mapping with priority scoring
  const categoryKeywords = {
    movies: {
      keywords: [
        "movie",
        "film",
        "cinema",
        "theater",
        "screening",
        "watch",
        "netflix",
        "show",
      ],
      priority: 1,
    },
    dining: {
      keywords: [
        "dinner",
        "lunch",
        "breakfast",
        "brunch",
        "meal",
        "eat",
        "restaurant",
        "food",
        "cuisine",
        "dine",
      ],
      priority: 1,
    },
    games: {
      keywords: [
        "game",
        "board",
        "card",
        "trivia",
        "escape",
        "arcade",
        "video",
        "gaming",
        "play",
        "puzzle",
      ],
      priority: 1,
    },
    outdoor: {
      keywords: [
        "hike",
        "beach",
        "park",
        "outdoor",
        "nature",
        "walk",
        "run",
        "cycle",
        "sport",
        "swim",
        "tennis",
        "football",
      ],
      priority: 1,
    },
    drinks: {
      keywords: [
        "coffee",
        "drink",
        "bar",
        "pub",
        "cocktail",
        "wine",
        "beer",
        "tea",
        "cafe",
        "brewery",
        "happy hour",
      ],
      priority: 1,
    },
    fitness: {
      keywords: [
        "gym",
        "workout",
        "yoga",
        "fitness",
        "exercise",
        "pilates",
        "dance",
        "martial",
        "crossfit",
        "training",
      ],
      priority: 1,
    },
  };

  let bestMatch = "games"; // Default fallback
  let highestScore = 0;

  // Score each category based on keyword matches
  for (const [category, data] of Object.entries(categoryKeywords)) {
    let score = 0;
    data.keywords.forEach((keyword) => {
      if (lowercaseText.includes(keyword)) {
        score += data.priority;
      }
    });

    if (score > highestScore) {
      highestScore = score;
      bestMatch = category;
    }
  }

  return bestMatch;
}
